import React, { useEffect } from 'react';

// Hooks
import { useWorkspaceSnapshotTracking } from 'Shared/tracking/use-workspace-snapshot-tracking';

// Types
import { WorkspaceMeeting } from 'Shared/types/WorkspaceMeeting.type';
import { ItemType } from 'Shared/constants/types';

// Utils
import { formatDate } from 'Shared/utils/DateTime.utils';

// Components
import { WpContainer } from 'Wpds/components/container';
import SnapshotMeetingItem from './SnapshotMeetingItem';
import SnapshotActionItems from './SnapshotActionItems';

// Styles
import './WorkspaceMeetingSnapshotItem.scss';

type Props = {
  meeting: WorkspaceMeeting;
  snapshot;
};

const WorkspaceMeetingSnapshotItem = ({ meeting, snapshot }: Props) => {
  const { trackViewedSnapshot } = useWorkspaceSnapshotTracking();

  const date = meeting.startTime;
  const currentYear = new Date().getFullYear();
  const isCurrentYear = currentYear.toString() === formatDate(date, 'YYYY');
  const d = formatDate(date, `dddd MMMM Do${isCurrentYear ? '' : ', YYYY'}`);
  const t = formatDate(date, `h:mma`);

  const formattedDate = `${d} at ${t}`;

  // do we need to sort on position?
  const meetingItems =
    snapshot &&
    snapshot.data.meetingItems
      .filter(item => item.itemType === ItemType.discussionTopic)
      .map(item => {
        const subItems = snapshot.data.meetingItems.filter(i => i.parentId === item.id);
        const newItem = { ...item, subItems };
        if (item.isSection || subItems.length > 0) {
          return newItem;
        }
        // if item has a parent, it was added to the parent object to a new attribute called `subItems` above,
        // therefore we do not want it in the list to render as a top level item
        if (item.parentId) return null;
        return newItem;
      })
      // filter out all the null generated from not sub items removed from top level list
      .filter(item => !!item);

  const actionItems =
    snapshot &&
    snapshot.data.meetingItems
      .filter(item => item.itemType === ItemType.actionItem)
      .map(item => {
        return {
          ...item,
          meetingItemAssignment:
            snapshot.data.meetingItemAssignments.find(assignment => assignment.meetingItemId === item.id) || {},
        };
      });

  useEffect(() => {
    snapshot && trackViewedSnapshot({ snapshot_id: snapshot.slug });
  }, [snapshot]);

  if (!snapshot) return null;

  const baseClassName = 'WorkspaceMeetingSnapshotItem';

  return (
    <div className={baseClassName}>
      <p className={`${baseClassName}__date`}>{formattedDate}</p>
      <WpContainer className={`${baseClassName}__agenda`}>
        {meetingItems.map(item => (
          <SnapshotMeetingItem key={item.uuid} meetingItem={item} snapshot={snapshot} workspaceMeeting={meeting} />
        ))}
      </WpContainer>
      <div className={`${baseClassName}__action-items`}>
        <SnapshotActionItems actionItems={actionItems} workspaceMeeting={meeting} />
      </div>
    </div>
  );
};

export default WorkspaceMeetingSnapshotItem;
